// Pure, framework-free filter model for the Resources directory. Mirrors
// solution-filters.ts: a state shape, a predicate over a FilterableResource, an
// active-count helper, and a URL codec. Everything here is unit-tested and has no
// React or router dependency.

export const RATING_MIN = 1;
export const RATING_MAX = 5;
export const POINTS_MIN = 0;

// Reservation filter = whether a Resource is reserved for an Account (Resource reservation;
// named apart from the Fit Score "Availability" dimension on purpose). It is a single
// choice: "reserved" and "unreserved" are complements, so picking both would
// be the same as picking neither.
export type ReservationFilter = "unreserved" | "reserved";
export const RESERVATION_FILTER_OPTIONS: ReadonlyArray<{
  value: ReservationFilter;
  label: string;
}> = [
  { value: "unreserved", label: "Not reserved" },
  { value: "reserved", label: "Reserved" },
];

function parseReservationFilter(v: unknown): ReservationFilter | null {
  return v === "unreserved" || v === "reserved" ? v : null;
}

export interface ResourceFilterState {
  search: string;
  disciplines: string[]; // discipline codes — OR
  constraints: string[]; // constraint ids — match-all at >= constraintMin
  constraintMin: number; // 1..5
  pointsMin: number; // load floor (>= POINTS_MIN)
  pointsMax: number | null; // load ceiling (null = unbounded)
  solutions: string[]; // solution ids — OR
  accounts: string[]; // account ids — OR
  reservationFilter: ReservationFilter | null; // null = either
}

export interface FilterableResource {
  name: string;
  email: string;
  points: number;
  constraints: Array<{ id: string; rating: number | null }>;
  disciplines: Array<{ code: string }>;
  solutions: Array<{ id: string }>;
  accounts: Array<{ id: string }>;
  // Only an in-force reservation counts: an expired reservation (`active: false`) reads as Not reserved.
  reservation: { active: boolean } | null;
}

export const DEFAULT_FILTERS: ResourceFilterState = {
  search: "",
  disciplines: [],
  constraints: [],
  constraintMin: RATING_MIN,
  pointsMin: POINTS_MIN,
  pointsMax: null,
  solutions: [],
  accounts: [],
  reservationFilter: null,
};

export function matchesResourceFilters(r: FilterableResource, s: ResourceFilterState): boolean {
  const q = s.search.trim().toLowerCase();
  if (q && !r.name.toLowerCase().includes(q) && !r.email.toLowerCase().includes(q)) return false;

  if (s.disciplines.length > 0) {
    const have = new Set(r.disciplines.map((d) => d.code));
    if (!s.disciplines.some((code) => have.has(code))) return false;
  }

  if (s.constraints.length > 0) {
    const rating = new Map(r.constraints.map((c) => [c.id, c.rating]));
    const ok = s.constraints.every((id) => {
      const v = rating.get(id);
      return v != null && v >= s.constraintMin;
    });
    if (!ok) return false;
  }

  if (r.points < s.pointsMin) return false;
  if (s.pointsMax != null && r.points > s.pointsMax) return false;

  if (s.solutions.length > 0) {
    const have = new Set(r.solutions.map((x) => x.id));
    if (!s.solutions.some((id) => have.has(id))) return false;
  }

  if (s.accounts.length > 0) {
    const have = new Set(r.accounts.map((x) => x.id));
    if (!s.accounts.some((id) => have.has(id))) return false;
  }

  if (s.reservationFilter !== null) {
    const reserved = r.reservation?.active === true;
    if (reserved !== (s.reservationFilter === "reserved")) return false;
  }

  return true;
}

export function activeFilterCount(s: ResourceFilterState): number {
  let n = 0;
  if (s.search.trim()) n++;
  if (s.disciplines.length) n++;
  if (s.constraints.length) n++;
  if (s.pointsMin !== POINTS_MIN || s.pointsMax != null) n++;
  if (s.solutions.length) n++;
  if (s.accounts.length) n++;
  if (s.reservationFilter !== null) n++;
  return n;
}

function csv(v: string[]): string {
  return v.join(",");
}
function splitCsv(v: unknown): string[] {
  return typeof v === "string" && v.length > 0 ? v.split(",").filter(Boolean) : [];
}

export function encodeFilters(s: ResourceFilterState): Record<string, string> {
  const out: Record<string, string> = {};
  if (s.search.trim()) out.q = s.search.trim();
  if (s.disciplines.length) out.disc = csv(s.disciplines);
  if (s.constraints.length) {
    out.con = csv(s.constraints);
    out.conMin = String(s.constraintMin);
  }
  if (s.pointsMin !== POINTS_MIN || s.pointsMax != null)
    out.load = `${s.pointsMin}-${s.pointsMax ?? ""}`;
  if (s.solutions.length) out.sol = csv(s.solutions);
  if (s.accounts.length) out.acct = csv(s.accounts);
  if (s.reservationFilter !== null) out.resv = s.reservationFilter;
  return out;
}

function clampRating(n: number, min: number): number {
  return Number.isInteger(n) && n >= RATING_MIN && n <= RATING_MAX ? n : min;
}

// Unknown keys are ignored on purpose: a stale saved filter (localStorage) or an old
// bookmarked URL may still carry the removed `comp` / `compMin` Competency params.
export function decodeFilters(search: Record<string, unknown>): ResourceFilterState {
  let pointsMin = POINTS_MIN;
  let pointsMax: number | null = null;
  if (typeof search.load === "string") {
    const [loRaw, hiRaw] = search.load.split("-");
    const lo = Number(loRaw);
    if (Number.isInteger(lo) && lo >= POINTS_MIN) pointsMin = lo;
    if (hiRaw !== undefined && hiRaw !== "") {
      const hi = Number(hiRaw);
      if (Number.isInteger(hi) && hi >= pointsMin) pointsMax = hi;
    }
  }
  return {
    search: typeof search.q === "string" ? search.q : "",
    disciplines: splitCsv(search.disc),
    constraints: splitCsv(search.con),
    constraintMin: clampRating(Number(search.conMin), RATING_MIN),
    pointsMin,
    pointsMax,
    solutions: splitCsv(search.sol),
    accounts: splitCsv(search.acct),
    reservationFilter: parseReservationFilter(search.resv),
  };
}
